import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Briefcase, Calendar, MapPin, DollarSign, Users, Bell, Plus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { CreateRFP } from '@/components/rfp/CreateRFP';
import { ViewProposals } from '@/components/rfp/ViewProposals';
import { SubmitProposal } from '@/components/rfp/SubmitProposal';

interface RFP {
  id: string;
  title: string;
  description: string;
  project_type: string;
  location?: string;
  estimated_volume?: number;
  timeline?: string;
  budget_range?: string;
  priority_level?: string;
  status: string;
  created_at: string;
  created_by: string;
  proposal_count?: number;
}

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  created_at: string;
  rfp_id?: string;
}

export const RFPsTab: React.FC = () => {
  const { user, profile } = useAuth();
  const [showCreateRFP, setShowCreateRFP] = useState(false);
  const [showProposals, setShowProposals] = useState(false);
  const [showSubmitProposal, setShowSubmitProposal] = useState(false);
  const [selectedRFP, setSelectedRFP] = useState<RFP | null>(null);
  const [rfps, setRfps] = useState<RFP[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch RFPs based on role
  const fetchRFPs = async () => {
    if (!user) return;

    try {
      let query = supabase
        .from('rfps')
        .select(`
          *,
          proposals(count)
        `)
        .order('created_at', { ascending: false });

      if (profile?.user_type === 'entity') {
        query = query.eq('created_by', user.id);
      } else if (profile?.user_type === 'project_developer') {
        query = query.eq('status', 'open').neq('created_by', user.id);
      }

      const { data, error } = await query;

      if (error) throw error;

      const rfpsWithCounts = data?.map(rfp => ({
        ...rfp,
        proposal_count: rfp.proposals?.[0]?.count || 0
      })) || [];

      setRfps(rfpsWithCounts);
    } catch (error) {
      console.error('Error fetching RFPs:', error);
    }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setNotifications(data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchRFPs(), fetchNotifications()]);
      setLoading(false);
    };

    loadData();
  }, [user]);

  const handleCreateRFP = () => {
    fetchRFPs();
    setShowCreateRFP(false);
  };

  const handleViewProposals = (rfp: RFP) => {
    setSelectedRFP(rfp);
    setShowProposals(true);
  };

  const handleSubmitProposalOpen = (rfp: RFP) => {
    setSelectedRFP(rfp);
    setShowSubmitProposal(true);
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'open': { color: 'bg-green-100 text-green-800', label: 'Open' },
      'closed': { color: 'bg-gray-100 text-gray-800', label: 'Closed' },
      'draft': { color: 'bg-yellow-100 text-yellow-800', label: 'Draft' }
    };
    
    const config = statusMap[status as keyof typeof statusMap] || statusMap.draft;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority: string = 'medium') => {
    const priorityMap = {
      'low': { color: 'bg-blue-100 text-blue-800', label: 'Low' },
      'medium': { color: 'bg-yellow-100 text-yellow-800', label: 'Medium' },
      'high': { color: 'bg-orange-100 text-orange-800', label: 'High' },
      'urgent': { color: 'bg-red-100 text-red-800', label: 'Urgent' }
    };
    
    const config = priorityMap[priority as keyof typeof priorityMap] || priorityMap.medium;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-32 bg-muted animate-pulse rounded-lg"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">{profile?.user_type === 'entity' ? 'My RFPs' : 'RFP Opportunities'}</h2>
          <p className="text-muted-foreground">
            {profile?.user_type === 'entity' ? 'Create RFPs and review developer proposals' : 'View and respond to entity RFPs'}
          </p>
        </div>
        {profile?.user_type === 'entity' && (
          <Button onClick={() => setShowCreateRFP(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create RFP
          </Button>
        )}
      </div>

      {/* Notifications */}
      {notifications.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-blue-800 flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Recent RFP Activities
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {notifications.slice(0, 3).map((notification) => (
                <div key={notification.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div>
                    <p className="font-medium">{notification.title}</p>
                    <p className="text-sm text-muted-foreground">{notification.message}</p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(notification.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue={profile?.user_type === 'entity' ? 'available-rfps' : 'available-rfps'} className="w-full">
        <TabsList>
          <TabsTrigger value="available-rfps">{profile?.user_type === 'entity' ? 'My RFPs' : 'Available RFPs'}</TabsTrigger>
          {profile?.user_type === 'project_developer' && (
            <TabsTrigger value="my-proposals">My Proposals</TabsTrigger>
          )}
        </TabsList>
        
        <TabsContent value="available-rfps" className="space-y-4">
          {rfps.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Briefcase className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">{profile?.user_type === 'entity' ? 'No RFPs Yet' : 'No RFPs Available'}</h3>
                <p className="text-muted-foreground mb-4">
                  {profile?.user_type === 'entity' ? 'Create your first RFP to receive proposals' : 'Check back later for new opportunities from entities'}
                </p>
                {profile?.user_type === 'entity' && (
                  <Button onClick={() => setShowCreateRFP(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create RFP
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {rfps.map((rfp) => (
                <Card key={rfp.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-lg">{rfp.title}</CardTitle>
                          {getStatusBadge(rfp.status)}
                          {getPriorityBadge(rfp.priority_level)}
                        </div>
                        <p className="text-muted-foreground">{rfp.description}</p>
                      </div>
                      {profile?.user_type === 'entity' ? (
                        <Button variant="outline" size="sm" onClick={() => handleViewProposals(rfp)}>
                          View Proposals
                        </Button>
                      ) : (
                        <Button variant="default" size="sm" onClick={() => handleSubmitProposalOpen(rfp)}>
                          Submit Proposal
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-muted-foreground" />
                        <span>{rfp.project_type}</span>
                      </div>
                      {rfp.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-muted-foreground" />
                          <span>{rfp.location}</span>
                        </div>
                      )}
                      {rfp.estimated_volume && (
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span>{rfp.estimated_volume.toLocaleString()} tCO₂e</span>
                        </div>
                      )}
                      {rfp.budget_range && (
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-muted-foreground" />
                          <span>{rfp.budget_range}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        <span>Posted {new Date(rfp.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{rfp.proposal_count || 0} proposals</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        {profile?.user_type === 'project_developer' && (
          <TabsContent value="my-proposals" className="space-y-4">
            <Card>
              <CardContent className="text-center py-12">
                <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">My Proposals</h3>
                <p className="text-muted-foreground">
                  Track your submitted proposals and their status
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Modals based on role */}
      {profile?.user_type === 'entity' && (
        <CreateRFP isOpen={showCreateRFP} onClose={() => setShowCreateRFP(false)} onSuccess={handleCreateRFP} />
      )}
      {profile?.user_type === 'entity' && selectedRFP && (
        <ViewProposals isOpen={showProposals} onClose={() => setShowProposals(false)} rfp={selectedRFP} />
      )}
      {profile?.user_type === 'project_developer' && selectedRFP && (
        <SubmitProposal isOpen={showSubmitProposal} onClose={() => setShowSubmitProposal(false)} rfp={selectedRFP} onSuccess={fetchRFPs} />
      )}
    </div>
  );
};